import React, { useEffect, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import MuiBox from "../MuiBox";

const CustomDatePicker = ({
  label,
  value,
  onChange,
  views,
  error,
  errorMsg,
  onError,
  format = "",
  labelClassName,
  variant,
  className,
  ...rest
}) => {
  const [showMsg, setShowMsg] = useState("");

  useEffect(() => {
    if (error) {
      setShowMsg(errorMsg);
    } else {
      setShowMsg("");
    }
  }, [error, errorMsg]);

  // Default to all views if none are provided
  const resolvedViews = views || ["year", "month", "day"];

  // Determine the appropriate format based on resolvedViews
  const getFormat = () => {
    if (resolvedViews.length === 3) return "DD-MM-YYYY";
    if (resolvedViews.includes("day") && resolvedViews.includes("month"))
      return "DD MMMM";
    if (resolvedViews.includes("month") && resolvedViews.includes("year"))
      return "MMMM YYYY";
    if (resolvedViews.includes("day")) return "DD";
    if (resolvedViews.includes("month")) return "MMMM";
    if (resolvedViews.includes("year")) return "YYYY";
    return "DD MMMM YYYY";
  };

  // Determine sizes based on variant
  const isLg = variant === "lg";
  const inputHeight = isLg ? "40px" : "32px";
  const inputFontSize = isLg ? "0.875rem" : "0.8125rem";

  return (
    <MuiBox sx={{ py: 0 }} className={className}>
      {label && (
        <label
          className={`block font-semibold text-[14px] ${labelClassName || ""}`}
        >
          {label}
        </label>
      )}
      <MuiBox sx={{ mt: 0, pt: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            className="date-picker-custom w-full"
            value={value}
            slotProps={{
              textField: {
                error: Boolean(error),
                readOnly: true,
                fullWidth: true,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    height: inputHeight,
                    borderRadius: "5px",
                    backgroundColor: "#fff",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                    width: "100%",

                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgba(226, 232, 240, 0.63)",
                    },

                    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid #ef4444 !important",
                    },

                    "& .MuiInputBase-input::placeholder": {
                      color: "hsl(240 3.8% 46.1% / 0.4)",
                      opacity: 1,
                    },

                    // Hover – match TimePicker
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgb(210 210 219 / 0.9)",
                    },

                    // Focus – match TimePicker
                    "&.Mui-focused": {
                      boxShadow:
                        "0 1px 2px 0 rgba(0, 0, 0, 0.05), 0 0 0 3px rgba(203, 213, 225, 0.25)",
                      border: "0.5px solid rgb(210 210 219 / 0.7)",
                    },

                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgb(210 210 219 / 0.9)",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 12px",
                    fontSize: inputFontSize,
                  },
                },
              },
            }}
            onChange={onChange}
            views={resolvedViews}
            format={format ? format : getFormat()}
            onError={(e) => {
              if (onError) {
                onError(e);
              }
              if (
                e === "invalidDate" ||
                e === "disableFuture" ||
                e === "disablePast"
              ) {
                setShowMsg("Please enter valid Date");
              } else if (e === "maxDate" || e === "minDate") {
                setShowMsg("Invalid Date Range");
              } else {
                setShowMsg(errorMsg);
              }
            }}
            emptyLabel={`Enter ${label}`}
            {...rest}
          />
          {showMsg && (
            <div className="text-[0.8125rem] text-red-500">{showMsg}</div>
          )}
        </LocalizationProvider>
      </MuiBox>
    </MuiBox>
  );
};

export default CustomDatePicker;